import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, Search, CheckCircle2, Clock, Flag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: "Pending dispatch", icon: Clock, cls: "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]" },
  confirmed: { label: "Visit confirmed", icon: CheckCircle2, cls: "border-sky-400/40 bg-sky-400/10 text-sky-400" },
  completed: { label: "Work completed", icon: Flag, cls: "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" },
};

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-[#FF2A3B]/60";

export const TrackBooking = () => {
  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleLookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/bookings/lookup`, {
        params: { ref: ref.trim(), email: email.trim() },
      });
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string" && err.response?.status !== 422
          ? detail
          : "No booking found — check your reference code and email address."
      );
    } finally {
      setLoading(false);
    }
  };

  const meta = result ? STATUS_META[result.status] || STATUS_META.pending : null;

  return (
    <section id="track" className="border-t border-white/10 bg-[#12141C] py-16 sm:py-20" data-testid="track-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            <p className="font-jbmono text-xs uppercase tracking-[0.25em] text-[#FF2A3B] font-semibold" data-testid="track-eyebrow">
              ✦ Already booked?
            </p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="track-heading">
              Track your request.
            </h2>
            <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed">
              Enter the reference code from your confirmation email and the email
              address you booked with. Live status, straight from the dispatch board.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-3"
          >
            <form onSubmit={handleLookup} className="flex flex-col sm:flex-row gap-3" data-testid="track-form">
              <input
                required
                placeholder="Reference code (e.g. 219ADC5F)"
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                maxLength={8}
                className={`${inputCls} sm:max-w-[220px] font-jbmono uppercase tracking-widest`}
                data-testid="track-reference-input"
              />
              <input
                required
                type="email"
                placeholder="Booking email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`${inputCls} flex-1`}
                data-testid="track-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="flex items-center justify-center gap-2 rounded-full bg-[#FF2A3B] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#E01F30] disabled:opacity-60"
                data-testid="track-submit-button"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={15} />}
                Check status
              </button>
            </form>

            {error && (
              <p className="mt-4 rounded-lg border border-[#FF2A3B]/30 bg-[#FF2A3B]/5 px-4 py-3 text-sm text-[#FF6B77]" data-testid="track-error">
                {error}
              </p>
            )}

            {result && meta && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="card-surface mt-5 rounded-xl p-6"
                data-testid="track-result"
              >
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-jbmono text-xs uppercase tracking-[0.2em] text-slate-500">
                      Reference <span className="text-white">{result.reference}</span>
                    </p>
                    <h3 className="mt-2 font-display text-lg font-bold tracking-tight" data-testid="track-result-service">
                      {result.service_label}
                    </h3>
                    <p className="mt-1 text-sm text-slate-400">
                      Hi {result.name} — scheduled for {result.preferred_date}, {result.time_slot_label}.
                    </p>
                  </div>
                  <span
                    className={`flex items-center gap-2 rounded-full border px-4 py-2 font-jbmono text-xs uppercase tracking-[0.15em] ${meta.cls}`}
                    data-testid="track-status-pill"
                  >
                    <meta.icon size={14} />
                    {meta.label}
                  </span>
                </div>
                <p className="mt-4 border-t border-white/5 pt-4 font-jbmono text-xs uppercase tracking-[0.2em] text-slate-500">
                  Questions? Call (513) 720-5882 and quote your reference
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
